import React, { useEffect, useState } from "react";
import { marketFetchJson } from "../arena/marketApi";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const fmtPrice = (p) => {
  if (p == null || Number.isNaN(p)) return "—";
  if (Math.abs(p) >= 1000) return p.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (Math.abs(p) >= 1) return p.toFixed(2);
  return p.toFixed(4);
};
const fmtPct = (p) => (p == null || Number.isNaN(p) ? "—" : `${p >= 0 ? "+" : ""}${p.toFixed(2)}%`);
const fmtCompact = (n) => {
  if (n == null || Number.isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (a >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (a >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return n.toFixed(0);
};
const signClass = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-zinc-400");

// perf% from `daysAgo` candles back to the latest close
function perfSince(candles, daysAgo) {
  if (!candles?.length) return null;
  const last = candles[candles.length - 1].close;
  const idx = Math.max(0, candles.length - 1 - daysAgo);
  const ref = candles[idx]?.open ?? candles[idx]?.close;
  if (!ref) return null;
  return ((last - ref) / ref) * 100;
}
function perfYTD(candles) {
  if (!candles?.length) return null;
  const year = new Date().getUTCFullYear();
  const first = candles.find((c) => new Date(c.timestamp).getUTCFullYear() === year);
  if (!first) return null;
  const last = candles[candles.length - 1].close;
  return ((last - first.open) / first.open) * 100;
}

export default function SymbolDetails({ base, quote, onOpenChart }) {
  const [candles, setCandles] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCandles(null);
    if (!base) return undefined;
    marketFetchJson(`${API_URL}/api/okx/ohlcv/${base}?timeframe=1d&limit=400`)
      .then((d) => !cancelled && setCandles(d.candles || []))
      .catch(() => !cancelled && setCandles([]));
    return () => {
      cancelled = true;
    };
  }, [base]);

  if (!base) {
    return <div className="p-3 text-xs text-zinc-500 border-t border-zinc-800">Select a symbol to see details.</div>;
  }

  const last = quote?.last;
  const chg = quote?.chg;
  const chgPct = quote?.chgPct;
  const vol24 = quote?.volCcy24h != null ? quote.volCcy24h * (quote.last || 0) : null;
  const avgVol30 =
    candles && candles.length
      ? (candles.slice(-30).reduce((s, c) => s + (c.volume || 0), 0) / Math.min(30, candles.length)) * (last || 0)
      : null;

  const perf = [
    { label: "1W", v: perfSince(candles, 7) },
    { label: "1M", v: perfSince(candles, 30) },
    { label: "3M", v: perfSince(candles, 90) },
    { label: "6M", v: perfSince(candles, 180) },
    { label: "YTD", v: perfYTD(candles) },
    { label: "1Y", v: perfSince(candles, 365) },
  ];

  return (
    <div className="border-t border-zinc-800 overflow-y-auto text-xs">
      <div className="p-3 space-y-3">
        {/* header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-zinc-700 text-[9px] flex items-center justify-center text-zinc-200">
              {base[0]}
            </span>
            <span className="font-semibold text-zinc-100">{base}USDT.P</span>
          </div>
          <button onClick={() => onOpenChart?.(base)} className="text-[10px] px-2 py-0.5 bg-zinc-800 rounded hover:bg-zinc-700">
            Open in chart
          </button>
        </div>
        <div className="text-[10px] text-zinc-500">
          {base} / Tether PERPETUAL FUTURES · OKX
          <div>Swap · Crypto</div>
        </div>

        {/* price */}
        <div>
          <div className="text-2xl font-mono tabular-nums text-zinc-100">
            {fmtPrice(last)} <span className="text-xs text-zinc-500">USDT</span>
          </div>
          <div className={`${signClass(chgPct)} tabular-nums`}>
            {chg != null ? `${chg >= 0 ? "+" : ""}${fmtPrice(chg)}` : "—"} {fmtPct(chgPct)}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-emerald-400 mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Market open
          </div>
        </div>

        {/* key stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-zinc-800/40 rounded p-2">
            <div className="text-[10px] text-zinc-500">Volume (24h)</div>
            <div className="font-mono text-zinc-200">${fmtCompact(vol24)}</div>
          </div>
          <div className="bg-zinc-800/40 rounded p-2">
            <div className="text-[10px] text-zinc-500">Avg Vol (30D)</div>
            <div className="font-mono text-zinc-200">${fmtCompact(avgVol30)}</div>
          </div>
        </div>

        {/* performance grid */}
        <div>
          <div className="text-[10px] uppercase text-zinc-500 mb-1">Performance</div>
          <div className="grid grid-cols-3 gap-1">
            {perf.map((p) => (
              <div
                key={p.label}
                className={`rounded p-2 text-center ${
                  p.v == null ? "bg-zinc-800/30" : p.v >= 0 ? "bg-emerald-500/10" : "bg-rose-500/10"
                }`}
              >
                <div className={`font-mono tabular-nums ${signClass(p.v)}`}>{p.v == null ? "—" : fmtPct(p.v)}</div>
                <div className="text-[9px] text-zinc-500">{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
